import {
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';

import ContextSelector from './ContextSelector';
import styles from './styles.module.scss';
import Tree from './Tree';
import { getNodeSafe } from './utils';
import { getUwiqNode, isUwiqTracemap, SnapshotInfo } from './uwiqUtils';

export interface GraphProps {
  traceMap: any;
}

const Graph = ({ traceMap }: GraphProps) => {
  const [query, setQuery] = useState('');
  const [isPreferredPlusVisible, setIsPreferredPlusVisible] = useState(false);
  const [selectedSnapshotIdx, setSelectedSnapshotIdx] = useState(0);

  const isUwiq = useMemo(
    () => (traceMap ? isUwiqTracemap(traceMap) : false),
    [traceMap],
  );

  const { node, error, snapshots } = useMemo(() => {
    if (!traceMap)
      return { node: null, error: null, snapshots: [] as SnapshotInfo[] };

    if (isUwiq) {
      return getUwiqNode(traceMap, selectedSnapshotIdx);
    }

    const legacy = getNodeSafe(traceMap, isPreferredPlusVisible);
    return { ...legacy, snapshots: [] as SnapshotInfo[] };
  }, [traceMap, isPreferredPlusVisible, isUwiq, selectedSnapshotIdx]);

  const handleOnChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.currentTarget.value);
  }, []);

  const handleOnPreferredChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setIsPreferredPlusVisible(event.currentTarget.checked);
    },
    [],
  );

  const handleSnapshotSelect = useCallback((idx: number) => {
    setSelectedSnapshotIdx(idx);
  }, []);

  return (
    <div className={styles.Wrapper}>
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <div className={styles.Header}>
            <TextField
              disabled={!!error}
              placeholder="Search..."
              size="small"
              variant="outlined"
              fullWidth
              value={query}
              onChange={handleOnChange}
            />
            {isUwiq ? (
              <ContextSelector
                snapshots={snapshots}
                selectedIdx={selectedSnapshotIdx}
                onSelect={handleSnapshotSelect}
              />
            ) : (
              <FormControlLabel
                disabled={!!error}
                control={
                  <Checkbox
                    disabled={!!error}
                    checked={isPreferredPlusVisible}
                    onChange={handleOnPreferredChange}
                    color="secondary"
                  />
                }
                label={
                  <Typography noWrap variant="body2">
                    Show Preferred Plus decisions
                  </Typography>
                }
              />
            )}
          </div>
          {node && <Tree node={node} query={query} />}
        </>
      )}
    </div>
  );
};

export default Graph;
